import React, { useEffect, useState } from 'react';
import { AlertTriangle, X, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * CIRedAlertBanner — a top-of-page red banner that appears the moment the
 * pytest health status flips red. The nightly worker is silent (no Slack /
 * email); this banner is the visual escalation so a regression can't slip
 * past you if you don't happen to scroll down to the CI Health Badge.
 *
 * Behaviour:
 *   - Polls `GET /api/admin/health/tests` every 60s.
 *   - Renders ONLY when `status === "red"` (any failing tests).
 *   - "Show failing tests" toggles an inline collapsible list of the first
 *     5 failing test IDs with a per-row Copy button — paste straight into
 *     `pytest <id>` for triage.
 *   - "View" scrolls the CI Health Badge into view + attention-pulses it.
 *   - Dismissable per run (localStorage), keyed by `ran_at` so a NEW red
 *     run re-shows the banner even if you dismissed the last one.
 */

const REFRESH_MS = 60 * 1000;
const DISMISS_KEY = 'hibff.ci-red-banner.dismissed-run';
const MAX_SHOWN = 5;

export const CIRedAlertBanner = () => {
  const [data, setData] = useState(null);
  const [dismissedRun, setDismissedRun] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; }
  });
  const [expanded, setExpanded] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiClient.request('/api/admin/health/tests');
        if (!cancelled) setData(res);
      } catch {
        /* silent — the badge itself surfaces errors */
      }
    };
    load();
    const int = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(int);
    };
  }, []);

  const isRed = data?.status === 'red' && (data?.failed || 0) > 0;
  const runId = data?.ran_at || '';
  const isDismissedForThisRun = dismissedRun && dismissedRun === runId;
  // Stale-run guard — a frozen snapshot (>48h old, e.g. production where the
  // nightly worker is disabled) must not scream red forever.
  const isStale = runId && (Date.now() - new Date(runId).getTime() > 48 * 3600 * 1000);

  if (!isRed || isDismissedForThisRun || isStale) return null;

  const failed = data.failed;
  const total = (data.passed || 0) + failed + (data.skipped || 0);
  const failingIds = Array.isArray(data.failing_ids) ? data.failing_ids : [];
  const shown = failingIds.slice(0, MAX_SHOWN);
  const overflow = Math.max(0, failingIds.length - MAX_SHOWN);

  const scrollToBadge = () => {
    const el = document.querySelector('[data-testid="ci-health-badge"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'box-shadow 0.3s';
      el.style.boxShadow = '0 0 0 4px rgba(220, 38, 38, 0.35)';
      setTimeout(() => { el.style.boxShadow = ''; }, 1600);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, runId); } catch { /* ignore */ }
    setDismissedRun(runId);
  };

  const copyId = async (id, idx) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((cur) => (cur === idx ? null : cur)), 1500);
    } catch {
      /* clipboard blocked — silently ignore */
    }
  };

  return (
    <div
      data-testid="ci-red-alert-banner"
      className="rounded-lg mb-4"
      style={{
        backgroundColor: '#FEF2F2',
        border: '2px solid #DC2626',
        color: '#7F1D1D',
      }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#DC262620' }}
          >
            <AlertTriangle size={18} style={{ color: '#DC2626' }} />
          </div>
          <div className="min-w-0">
            <div
              data-testid="ci-red-alert-headline"
              className="text-sm font-bold"
              style={{ color: '#7F1D1D' }}
            >
              {failed} pytest {failed === 1 ? 'test is' : 'tests are'} failing · {data.passed}/{total} passing
            </div>
            <div className="text-xs mt-0.5" style={{ color: '#991B1B' }}>
              The nightly test suite went red. Investigate before the next release.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {failingIds.length > 0 && (
            <button
              data-testid="ci-red-alert-toggle"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold transition-colors"
              style={{ backgroundColor: '#FFFFFF', color: '#991B1B', border: '1px solid #DC2626' }}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {expanded ? 'Hide' : 'Show'} failing tests
            </button>
          )}
          <button
            data-testid="ci-red-alert-view-btn"
            onClick={scrollToBadge}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors"
            style={{ backgroundColor: '#DC2626', color: 'white' }}
          >
            View badge
          </button>
          <button
            data-testid="ci-red-alert-dismiss"
            onClick={dismiss}
            className="p-1.5 rounded hover:bg-red-100 transition-colors"
            title="Dismiss until the next run"
          >
            <X size={16} style={{ color: '#991B1B' }} />
          </button>
        </div>
      </div>

      {expanded && failingIds.length > 0 && (
        <div
          data-testid="ci-red-alert-failing-list"
          className="border-t px-3 py-2"
          style={{ borderColor: '#DC262640' }}
        >
          <ul className="space-y-1">
            {shown.map((id, idx) => (
              <li
                key={id + idx}
                data-testid={`ci-red-alert-failing-row-${idx}`}
                className="flex items-center justify-between gap-2 p-2 rounded"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #DC262620' }}
              >
                <code
                  className="text-xs truncate flex-1 font-mono"
                  style={{ color: '#7F1D1D' }}
                >
                  {id}
                </code>
                <button
                  data-testid={`ci-red-alert-copy-${idx}`}
                  onClick={() => copyId(id, idx)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold shrink-0 transition-colors"
                  style={{
                    backgroundColor: copiedIdx === idx ? '#2EAA5C' : '#F5F5F5',
                    color: copiedIdx === idx ? 'white' : '#333333',
                  }}
                  title="Copy this test ID"
                >
                  {copiedIdx === idx ? <Check size={12} /> : <Copy size={12} />}
                  {copiedIdx === idx ? 'Copied' : 'Copy'}
                </button>
              </li>
            ))}
          </ul>
          {overflow > 0 && (
            <p
              data-testid="ci-red-alert-overflow"
              className="text-xs mt-2"
              style={{ color: '#991B1B' }}
            >
              …plus {overflow} more. Run `pytest tests/ -rfE` for the full list.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CIRedAlertBanner;
